import React, { useState, useRef, useEffect } from "react";
import { Tooltip } from "@mui/material";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import DeleteIcon from "@mui/icons-material/Delete";
import { DisplayDateTime } from "components/DisplayDate";
import StickyNote2Icon from "@mui/icons-material/StickyNote2";
import UploadFile from "../../../components/Input/UploadFile";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { API_URL } from "const/constants";
import axios from "axios";
import { toast } from "react-toastify";
import { deleteData } from "const/axios";
import AttachmentChip from "components/Attachments/AttachmentChip";
import { useDraftAttachmentPaste } from "hooks/useAttachmentUploads";
import { parseAttachmentList, serializeAttachmentList } from "utils/attachmentClipboard";

const ClientNotes = () => {
  const [text, setText] = useState("");
  const [attachments, setAttachments] = useState();
  const { isUploading, onPaste: handlePaste } = useDraftAttachmentPaste({
    draft: attachments,
    setDraft: setAttachments,
  });
  const [notes, setNotes] = useState([]);
  const [clientData, setClientData] = useState(null);
  const buttonRef = useRef(null);
  const userDetail = JSON.parse(localStorage.getItem("userDetail"));
  const { id } = useParams();
  const queryClient = useQueryClient();
  const token = localStorage.getItem("token");

  // Fetch client data
  const { data: clientResponse, isLoading: isClientLoading } = useQuery({
    queryKey: ["client", id],
    queryFn: () => axios.get(`${API_URL}/clients/${id}`),
    enabled: !!id,
  });

  useEffect(() => {
    if (clientResponse?.data?.data) {
      setClientData(clientResponse.data.data);
    }
  }, [clientResponse]);

  const { data: notesData } = useQuery({
    queryKey: ["notes", id],
    queryFn: () =>
      axios.get(`${API_URL}/discussion/${id}`, {
        params: {
          type: "client",
        },
      }),
  });

  // Handle data when it's fetched
  useEffect(() => {
    if (notesData?.data?.comments) {
      setNotes(notesData?.data?.comments);
    }
  }, [notesData]);

  const { mutate, isPending } = useMutation(postData, {
    onSuccess(response, variables) {
      toast.success("Note added successfully");
      // Refetch the notes data
      queryClient.invalidateQueries({ queryKey: ["notes", id] });
      setText("");
      setAttachments([]);
    },
    onError() {
      toast.error("Error Submitting Comment");
    },
  });

  async function postData(payload) {
    await axios.post(`${API_URL}/discussion/${id}`, payload);
  }

  const submitPost = (e) => {
    e.preventDefault();

    if (!text.trim() && (!attachments?.attachments || attachments?.attachments?.length === 0)) {
      return;
    }
    const newNote = {
      text: text,
      type: "client",
      attachments: serializeAttachmentList(attachments?.attachments),
      attachment: serializeAttachmentList(attachments?.attachments),
      uuid: crypto.randomUUID(),
      createdBy: userDetail?.name ?? userDetail?.firstName,
      createdDate: new Date(),
    };

    mutate(newNote);
  };

  const deleteIndividualComment = (commentId) => {
    if (window.confirm("Are you sure you want to delete this note?")) {
      deleteData(
        `${API_URL}/discussion/comment/${commentId}`,
        () => {
          setNotes((prev) => prev.filter((item) => item?.id !== commentId));
        },
        token
      );
    }
  };

  return (
    <div className="flex flex-col h-full bg-app-surface-muted">
      {/* Header */}
      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Notes Display Area */}
        <div className="flex-1 overflow-y-auto p-6">
          {notes?.length > 0 ? (
            <div className="space-y-4">
              {notes?.map((arg) => {
                return (
                  <div id="chatbox" className="group relative" key={arg?.id}>
                    {userDetail?.name === arg?.postedBy ? (
                      <div className="flex flex-col items-end">
                        {(arg?.text !== "" || arg?.attachment) && (
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-sm text-app-text-secondary">
                              <DisplayDateTime value={arg?.createdDate} fallback="" />
                            </span>
                            {userDetail?.name === arg?.postedBy && (
                              <Tooltip title="Delete this comment">
                                <IconButton
                                  size="small"
                                  onClick={() => deleteIndividualComment(arg?.id)}
                                  className="opacity-0 group-hover:opacity-100 transition-opacity"
                                >
                                  <DeleteIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>
                            )}
                          </div>
                        )}

                        {(arg?.text !== "" || arg?.attachment) && (
                          <div className="flex flex-col items-end gap-2">
                            {arg?.text && (
                              <div className="bg-blue-500 text-white rounded-lg py-1 px-2">
                                <p className="whitespace-pre-wrap break-words">{arg?.text}</p>
                              </div>
                            )}
                            {arg?.attachment && (
                              <div className="flex flex-wrap gap-2 justify-end mt-1 w-fit">
                                {parseAttachmentList(arg.attachment).map((url) => (
                                  <AttachmentChip key={url} url={url} />
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="flex flex-col">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm font-medium text-app-text">
                            {arg?.createdBy}
                          </span>
                          <span className="text-sm text-app-text-secondary">
                            <DisplayDateTime value={arg?.createdDate} fallback="" />
                          </span>
                          {userDetail?.name === arg?.createdBy && (
                            <Tooltip title="Delete this comment">
                              <IconButton
                                size="small"
                                onClick={() => deleteIndividualComment(arg?.id)}
                                className="opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                <DeleteIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </div>
                        <div className="flex flex-col gap-2">
                          <div className="bg-app-surface-muted rounded-2xl py-2 px-4 w-fit">
                            <p className="whitespace-pre-wrap break-words text-app-text">
                              {arg?.text}
                            </p>
                          </div>
                          {arg?.attachment && (
                            <div className="flex flex-wrap gap-2 mt-1">
                              {parseAttachmentList(arg.attachment).map((url) => (
                                <AttachmentChip key={url} url={url} />
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-app-text-secondary">
              <StickyNote2Icon sx={{ fontSize: 48, color: "gray" }} />
              <p className="mt-4 text-lg">No notes yet</p>
              <p className="text-sm">Start the conversation by adding a note</p>
            </div>
          )}
          <div ref={buttonRef} />
        </div>

        {/* Notes Input Area */}
        <div className="bg-app-surface border-t border-app-border p-3">
          <form onSubmit={submitPost} className="space-y-4">
            <div className="bg-app-surface rounded-lg border border-app-border">
              <textarea
                id="note"
                rows="8"
                className="w-full px-4 py-3 text-app-text border-0 focus:ring-0 focus:outline-none  rounded-t-lg"
                placeholder="Write a note... or paste files to attach them"
                onChange={(e) => setText(e.target.value)}
                onPaste={handlePaste}
                value={text}
              />
            </div>

            <div className="bg-app-surface rounded-lg border border-app-border p-4">
              <UploadFile
                data={attachments}
                setData={setAttachments}
                imageKey="attachments"
                label="Attachments"
                type="multiple"
                newDesign={true}
              />
            </div>

            <div className="flex justify-end">
              <Button
                variant="contained"
                type="submit"
                disabled={
                  (!text.trim() && !attachments?.attachments?.length) || isPending || isUploading
                }
                sx={{
                  px: 4,
                  py: 1,
                  borderRadius: 2,
                  textTransform: "none",
                  fontSize: "1rem",
                }}
              >
                {isPending ? "Saving..." : isUploading ? "Uploading..." : "Save Note"}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ClientNotes;
